import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AuthLayout, { FormError, PasswordInput, TextLink } from '../components/AuthLayout';

import { API } from '../lib/api';

const MIN_PASSWORD = 10;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const longEnough = password.length >= MIN_PASSWORD;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!longEnough) {
      setError(`Use at least ${MIN_PASSWORD} characters for your new password.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match. Type them again.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success('Password updated. Sign in with your new password.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || "That reset link didn't work. Request a new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      footer={<>Link expired? <TextLink to="/forgot-password">Request a new one</TextLink></>}
      backTo="/login"
      backLabel="Back to sign in"
    >
      {!token ? (
        <FormError>This reset link is missing its code. Request a new link from the sign-in page.</FormError>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <FormError>{error}</FormError>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reset-password">New password</Label>
            <PasswordInput
              id="reset-password"
              autoComplete="new-password"
              aria-describedby="reset-password-rule"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p
              id="reset-password-rule"
              className={`m-0 flex items-center gap-1.5 text-sm leading-5 ${longEnough ? 'font-bold text-sev-low' : 'text-ink-muted'}`}
            >
              {longEnough && <Check className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
              At least {MIN_PASSWORD} characters
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reset-confirm">Type it again</Label>
            <PasswordInput
              id="reset-confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          <Button type="submit" size="lg" disabled={loading} className="mt-1 w-full">
            {loading ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
